import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  School,
  LocationCity,
  Class,
  CheckCircle,
} from '@mui/icons-material';
import locationService, { Country, Council, School as SchoolType, Class as ClassType } from '../services/locationService';
import courseService from '../services/courseService';
import courseEnrollmentService from '../services/courseEnrollmentService';
import authService from '../services/authService';
import type { Course } from '../types';

const SelfRegistration: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId'); // For parent registering a child
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Form data
  const [countries, setCountries] = useState<Country[]>([]);
  const [councils, setCouncils] = useState<Council[]>([]);
  const [schools, setSchools] = useState<SchoolType[]>([]);
  const [classes, setClasses] = useState<ClassType[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<number | ''>('');
  const [selectedCouncil, setSelectedCouncil] = useState<number | ''>('');
  const [selectedSchool, setSelectedSchool] = useState<number | ''>('');
  const [selectedClass, setSelectedClass] = useState<number | ''>('');
  const [selectedCourse, setSelectedCourse] = useState<number | ''>('');

  const steps = ['Select Country', 'Select Council', 'Select School', 'Select Class', 'Select Course', 'Confirm'];

  useEffect(() => {
    loadCountries();
    loadCourses();
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      loadCouncils(selectedCountry as number);
    } else {
      setCouncils([]);
      setSelectedCouncil('');
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCouncil) {
      loadSchools(selectedCouncil as number);
    } else {
      setSchools([]);
      setSelectedSchool('');
    }
  }, [selectedCouncil]);

  useEffect(() => {
    if (selectedSchool) {
      loadClasses(selectedSchool as number);
    } else {
      setClasses([]);
      setSelectedClass('');
    }
  }, [selectedSchool]);

  const loadCountries = async () => {
    try {
      setLoading(true);
      const data = await locationService.getCountries();
      setCountries(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load countries');
    } finally {
      setLoading(false);
    }
  };

  const loadCouncils = async (countryId: number) => {
    try {
      setLoading(true);
      const data = await locationService.getCouncils(countryId);
      setCouncils(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load councils');
    } finally {
      setLoading(false);
    }
  };

  const loadSchools = async (councilId: number) => {
    try {
      setLoading(true);
      const data = await locationService.getSchoolsByCouncil(councilId);
      setSchools(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async (schoolId: number) => {
    try {
      setLoading(true);
      const data = await locationService.getClassesBySchool(schoolId);
      setClasses(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const data = await courseService.getAvailableCourses();
      // Scottish secondary self-registration only applies to OPEN courses.
      const secondaryCourses = data.filter(
        (course) =>
          course.courseType === 'SECONDARY_SCHOOL' &&
          String(course.registrationMethod || 'CODE').toUpperCase() === 'OPEN'
      );
      setCourses(secondaryCourses);
    } catch (err: any) {
      console.error('Error loading courses:', err);
    }
  };

  const handleNext = () => {
    setError('');
    if (activeStep === 0 && !selectedCountry) {
      setError('Please select a country');
      return;
    }
    if (activeStep === 1 && !selectedCouncil) {
      setError('Please select a council');
      return;
    }
    if (activeStep === 2 && !selectedSchool) {
      setError('Please select a school');
      return;
    }
    if (activeStep === 3 && !selectedClass) {
      setError('Please select a class');
      return;
    }
    if (activeStep === 4 && !selectedCourse) {
      setError('Please select a course');
      return;
    }
    if (activeStep === 5) {
      handleSubmit();
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const userId = childId ? parseInt(childId, 10) : authService.getCurrentUser()?.id;
      if (!userId) {
        throw new Error('User not found');
      }

      const currentUser = authService.getCurrentUser();
      const enrolledBy = currentUser?.email || 'self';

      await courseEnrollmentService.enrollUserInCourse(
        userId,
        selectedCourse as number,
        enrolledBy,
        selectedClass as number // Pass classId for secondary school enrollment
      );

      // Success - navigate to dashboard
      if (childId) {
        navigate('/parent-dashboard');
      } else {
        navigate('/student-dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Your Country
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value as number)}
                label="Country"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {countries.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No countries available. Please contact support.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Your Council
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Council</InputLabel>
              <Select
                value={selectedCouncil}
                onChange={(e) => setSelectedCouncil(e.target.value as number)}
                label="Council"
                disabled={!selectedCountry || councils.length === 0}
              >
                {councils.map((council) => (
                  <MenuItem key={council.id} value={council.id}>
                    {council.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {councils.length === 0 && selectedCountry && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No councils found for the selected country.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Your School
            </Typography>
            <FormControl fullWidth>
              <InputLabel>School</InputLabel>
              <Select
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value as number)}
                label="School"
                disabled={!selectedCouncil || schools.length === 0}
              >
                {schools.map((school) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {schools.length === 0 && selectedCouncil && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No schools found for the selected council.
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Your Class
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value as number)}
                label="Class"
                disabled={!selectedSchool || classes.length === 0}
              >
                {classes.map((classItem) => (
                  <MenuItem key={classItem.id} value={classItem.id}>
                    {classItem.name} ({classItem.academicYear})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {classes.length === 0 && selectedSchool && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No classes found for the selected school.
              </Alert>
            )}
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Select Course
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value as number)}
                label="Course"
              >
                {courses.map((course) => (
                  <MenuItem key={course.id} value={course.id}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {courses.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No courses available. Please contact your school administrator.
              </Alert>
            )}
          </Box>
        );

      case 5:
        const selectedCountryName = countries.find((c) => c.id === selectedCountry)?.name;
        const selectedCouncilName = councils.find((c) => c.id === selectedCouncil)?.name;
        const selectedSchoolName = schools.find((s) => s.id === selectedSchool)?.name;
        const selectedClassName = classes.find((c) => c.id === selectedClass)?.name;
        const selectedCourseName = courses.find((c) => c.id === selectedCourse)?.name;

        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Confirm Registration
            </Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Country
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedCountryName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Council
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedCouncilName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      School
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedSchoolName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Class
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedClassName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Course
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedCourseName}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Alert severity="info">
              By confirming, you will be registered for the selected course. You can start learning
              immediately after registration.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            {childId ? 'Register Child for Course' : 'Self-Registration'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete the following steps to register for a course.
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            endIcon={
              loading ? (
                <CircularProgress size={20} />
              ) : activeStep === steps.length - 1 ? (
                <CheckCircle />
              ) : (
                <ArrowForward />
              )
            }
          >
            {activeStep === steps.length - 1 ? 'Confirm Registration' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SelfRegistration;

